import { calendarV2Copy } from "./calendarV2Copy";
import { RUNNING_VARIANTS, runningBpm, validPlannedExertion } from "./runningTraining";

// Player-only presentation. Never fill missing settings from editor defaults.
export const runningPlayerCopy = {
  de: {
    profile: "Belastungsprofil", intensity: "Intensität", effort: "Belastung",
    approximate: "Näherung aus historischer RPE · kein exakter Planwert",
    planned: "Geplant", actual: "Tatsächlich", higher: "Höher als geplant", same: "Wie geplant", lower: "Niedriger als geplant",
    heart: "HF-Zone", percent: "HFmax", target: "Ziel", percentUnit: "Zielbereich", flow: "Ablauf", summary: "Kurzbeschreibung", missing: "Keine Angabe", notApplicable: "Nicht anwendbar",
    warmup: "Einlaufen", sprintWarmup: "Aufwärmen", main: "Hauptteil", rest: "Pause",
    cooldown: "Auslaufen", duration: "Dauer", easy: "Leicht", moderate: "Mittel", hard: "Hart",
    varied: "Wechselnd", veryHard: "Sehr hart", minute: "Minute", minutes: "Minuten",
    second: "Sekunde", seconds: "Sekunden", repetition: "Wiederholung", repetitions: "Wiederholungen",
    sprint: "Sprint", sprints: "Sprints", each: "à", betweenIntervals: "zwischen den Wiederholungen",
    betweenSprints: "zwischen den Sprints", perInterval: "pro Intervall", perSprint: "pro Sprint",
    bpm: "Schläge/Min. · Orientierung",
    recovery_run: "Lockerer Lauf zur aktiven Erholung ohne Tempodruck.",
    endurance_run: "Konstanter Lauf im ruhigen Grundlagentempo.",
    tempo_run: "Zügiger, gleichmäßiger Lauf mit kontrolliert hoher Belastung.",
    interval_run: "Belastungsintervalle mit Erholung zwischen den Wiederholungen.",
    sprint_acceleration: "Kurze explosive Sprints mit vollständigen Pausen zur Qualitätssicherung.",
  },
  en: {
    profile: "Effort profile", intensity: "Intensity", effort: "Load",
    approximate: "Approximation from historical RPE · not an exact plan",
    planned: "Planned", actual: "Actual", higher: "Higher than planned", same: "As planned", lower: "Lower than planned",
    heart: "HR Zone", percent: "HRmax", target: "Target", percentUnit: "Target range", flow: "Session flow", summary: "Summary", missing: "Not specified", notApplicable: "Not applicable",
    warmup: "Warm-up", sprintWarmup: "Warm-up", main: "Main set", rest: "Rest",
    cooldown: "Cool-down", duration: "Duration", easy: "Easy", moderate: "Moderate", hard: "Hard",
    varied: "Varied", veryHard: "Very hard", minute: "minute", minutes: "minutes",
    second: "second", seconds: "seconds", repetition: "repetition", repetitions: "repetitions",
    sprint: "sprint", sprints: "sprints", each: "of", betweenIntervals: "between repetitions",
    betweenSprints: "between sprints", perInterval: "per interval", perSprint: "per sprint",
    bpm: "beats/min · guidance",
    recovery_run: "Easy run for active recovery without pace pressure.",
    endurance_run: "Steady run at an easy aerobic pace.",
    tempo_run: "Steady tempo run with a controlled higher effort.",
    interval_run: "Work intervals with recovery between repetitions.",
    sprint_acceleration: "Short explosive sprints with full recovery to maintain quality.",
  },
};

function numeric(value, min, max) {
  if (!["number", "string"].includes(typeof value) || String(value).trim() === "") return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= min && number <= max ? number : null;
}

function range(low, high, min, max) {
  const a = numeric(low, min, max); const b = numeric(high, min, max);
  // A single bound is not an exact value: omit incomplete ranges.
  return a !== null && b !== null && a <= b ? [a, b] : null;
}

function durationMinutes(item) {
  if (item.start_at && item.end_at) {
    const minutes = (Date.parse(item.end_at) - Date.parse(item.start_at)) / 60000;
    if (Number.isFinite(minutes) && minutes > 0 && minutes <= 1440) return minutes;
  }
  return numeric(item.duration_minutes ?? item.duration, 0.1, 1440);
}

export function runningDetailNote(item) {
  const description = item.player_visible_description;
  if ((item.training_unit_type || item.unit_type) === "running_training" && RUNNING_VARIANTS.includes(item.running_variant)
      && typeof description === "string" && ["de", "en"].some((lang) =>
        description.trim() === calendarV2Copy[lang][`runningDescription_${item.running_variant}`])) {
    // The localized summary replaces only the exact built-in description.
    // Individually entered notes (including amended presets) remain untouched.
    return null;
  }
  return description;
}

export function runningPresentation(item, language = "de") {
  if (!item || (item.training_unit_type || item.unit_type) !== "running_training"
      || !RUNNING_VARIANTS.includes(item.running_variant)) return null;
  const lang = language === "en" ? "en" : "de";
  const copy = runningPlayerCopy[lang];
  const d = item.running_details && typeof item.running_details === "object" && !Array.isArray(item.running_details)
    ? item.running_details : {};
  const variant = item.running_variant;
  const sprint = variant === "sprint_acceleration";
  const interval = variant === "interval_run";
  const fmt = (n) => n.toLocaleString(lang, { maximumFractionDigits: 6 });
  const rangeText = ([a, b]) => a === b ? fmt(a) : `${fmt(a)}–${fmt(b)}`;
  const unit = (n, singular, plural) => `${fmt(n)} ${copy[n === 1 ? singular : plural]}`;
  const minutes = (n) => unit(n, "minute", "minutes");
  const profile = ["effort", "heart", "percent"].map((key) => ({ key, value: null, hint: null }));
  const profileNotes = [];
  const setProfile = (key, value, hint = null) => Object.assign(profile.find((entry) => entry.key === key), { value, hint });
  let instruction = null;
  if (typeof d.intensity === "string" && d.intensity.trim()) {
    // Avoid repeating generic preset intensity alongside load; retain custom instructions.
    const preset = RUNNING_VARIANTS.find((key) => ["de", "en"].some((locale) =>
      calendarV2Copy[locale][`runningIntensity_${key}`] === d.intensity.trim()));
    if (!preset) instruction = d.intensity;
  }
  const effort = range(d.rpe_min, d.rpe_max, 1, 10);
  const explicitPlan = validPlannedExertion(d.planned_exertion);
  if (explicitPlan) {
    setProfile("effort", `${d.planned_exertion}/5`, calendarV2Copy[lang][`runningLoad_${d.planned_exertion}`]);
  } else if (d.planned_exertion == null && effort) {
    // Display-only: halve the historical range's midpoint and round up.
    // This approximation never enters a write payload or a feedback comparison.
    const approximate = Math.ceil((effort[0] + effort[1]) / 4);
    setProfile("effort", `≈ ${approximate}/5`, calendarV2Copy[lang][`runningLoad_${approximate}`]);
    profileNotes.push(`${copy.approximate} (${rangeText(effort)}/10)`);
  }
  const feedback = item.training_feedback;
  const comparison = explicitPlan && typeof item.training_feedback_id === "string" && item.training_feedback_id.trim()
    && feedback?.submitted_at && Number.isFinite(Date.parse(feedback.submitted_at)) && validPlannedExertion(feedback.exertion)
    ? { planned: d.planned_exertion, actual: feedback.exertion,
      label: copy[feedback.exertion > d.planned_exertion ? "higher" : feedback.exertion < d.planned_exertion ? "lower" : "same"] } : null;
  if (!sprint) {
    const zone = typeof d.hr_zone === "string" && /^[1-5](?:[-–][1-5])?$/.test(d.hr_zone.trim())
      ? d.hr_zone.trim().split(/[-–]/).map(Number) : [];
    const percent = range(d.hr_percent_min, d.hr_percent_max, 1, 100);
    const measured = numeric(d.measured_hrmax, 80, 240);
    const bpm = percent && measured !== null ? runningBpm(d, variant) : null;
    if (zone.length && zone[0] <= zone[zone.length - 1]) {
      setProfile("heart", rangeText([zone[0], zone[zone.length - 1]]));
      if (bpm) profileNotes.push(`${rangeText(bpm)} ${copy.bpm}`);
    }
    if (percent) setProfile("percent", `${rangeText(percent)} %`);
  }
  // Sprint targets are distance/repetitions, never heart-rate zones, even on legacy rows.
  profile.forEach((entry) => { if (entry.value === null) entry.missingLabel = sprint && entry.key !== "effort" ? copy.notApplicable : copy.missing; });
  const steps = [];
  const add = (key, value) => { if (value) steps.push({ key, value }); };
  let summary = copy[variant];
  if (interval || sprint) {
    const warmup = numeric(d.warmup_minutes, 0, 120);
    const rawReps = numeric(d.repetitions, 1, 100);
    const reps = Number.isInteger(rawReps) ? rawReps : null;
    const work = numeric(sprint ? d.distance_m : d.work_minutes, sprint ? 1 : 0.1, sprint ? 1000 : 120);
    const rest = numeric(sprint ? d.rest_seconds : d.rest_minutes, 0, sprint ? 600 : 120);
    const cooldown = numeric(d.cooldown_minutes, 0, 120);
    const workText = work === null ? null : sprint ? `${fmt(work)} m` : minutes(work);
    const repsText = reps === null ? null : unit(reps, sprint ? "sprint" : "repetition", sprint ? "sprints" : "repetitions");
    add(sprint ? "sprintWarmup" : "warmup", warmup > 0 ? minutes(warmup) : null);
    add("main", repsText && workText ? `${repsText} ${copy.each} ${workText}`
      : repsText || (workText ? `${workText} ${copy[sprint ? "perSprint" : "perInterval"]}` : null));
    // There is no between-repetitions pause for one repetition.
    add("rest", rest !== null && reps !== 1 ? `${sprint ? unit(rest, "second", "seconds") : minutes(rest)} ${copy[sprint ? "betweenSprints" : "betweenIntervals"]}` : null);
    if (interval) {
      add("cooldown", cooldown > 0 ? minutes(cooldown) : null);
      if (reps !== null && work !== null) {
        const parts = lang === "de" ? [
          `${warmup > 0 ? `Nach ${minutes(warmup)} Einlaufen ${reps === 1 ? "folgt" : "folgen"}` : reps === 1 ? "Geplant ist" : "Geplant sind"} ${repsText} mit je ${minutes(work)}.`,
          rest !== null && reps > 1 ? `Zwischen den Intervallen jeweils ${minutes(rest)} Pause.` : null,
          cooldown > 0 ? `Zum Abschluss ${minutes(cooldown)} Auslaufen.` : null,
        ] : [
          `${warmup > 0 ? `After a ${fmt(warmup)}-minute warm-up, complete` : "Complete"} ${fmt(reps)} work ${reps === 1 ? "interval" : "intervals"} of ${minutes(work)} each.`,
          rest !== null && reps > 1 ? `Recover for ${minutes(rest)} between intervals.` : null,
          cooldown > 0 ? `Finish with a ${fmt(cooldown)}-minute cool-down.` : null,
        ];
        summary = parts.filter(Boolean).join(" ");
      }
    }
  } else {
    const duration = durationMinutes(item);
    add("duration", duration === null ? null : minutes(duration));
  }
  return { copy, profile, profileNotes, instruction, steps, summary, comparison };
}
